import { ActionIcon, Stack, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import { GridColDef } from "@mui/x-data-grid";
import { PencilIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";

import { DataGridBase } from "@/components/DataGridBase";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { Customer, Role, useDeleteCustomerMutation, useGetCustomersQuery } from "@/store";
import { cn } from "@/utils";
import { requireRoles } from "@/utils/requiredRoles";

export const Route = createFileRoute("/_private/dashboard/_base/clientes/")({
  component: RouteComponent,
  beforeLoad: requireRoles({ allowed: [Role.Usuario] })
});

function RouteComponent() {
  const navigate = useNavigate();
  const { data = [], isFetching, refetch } = useGetCustomersQuery();
  const [trigger, { isLoading }] = useDeleteCustomerMutation();

  const handleDelete = (id: number) => {
    modals.openConfirmModal({
      title: "Confirmação de Exclusão",
      children: <p>Tem certeza que deseja excluir o cliente?</p>,
      labels: { confirm: "Excluir", cancel: "Cancelar" },
      groupProps: { className: cn("!grid grid-cols-1 md:grid-cols-2") },
      cancelProps: { disabled: isLoading },
      confirmProps: { color: "red", loading: isLoading },
      onConfirm: async () => {
        await trigger({ id });
      }
    });
  };

  const columns: GridColDef<Customer>[] = [
    { field: "id", headerName: "ID", minWidth: 100 },
    {
      field: "name",
      headerName: "Nome",
      minWidth: 150,
      flex: 1,
      display: "flex",
      renderCell: ({ row }) => {
        const { code, name } = row;

        return (
          <div className="flex flex-col">
            <p>
              <strong>Código:</strong> {code}
            </p>
            <p>{name}</p>
          </div>
        );
      }
    },
    { field: "code", headerName: "Código", minWidth: 150 },
    { field: "cnpj", headerName: "CNPJ", minWidth: 150 },
    {
      field: "city",
      headerName: "Cidade",
      minWidth: 150
    },
    {
      field: "actions",
      headerName: "Ações",
      type: "actions",
      minWidth: 100,
      renderCell: ({ row }) => {
        return (
          <ActionIcon.Group>
            <Tooltip label="Editar Cliente">
              <ActionIcon variant="light" color="yellow" onClick={() => navigate({ to: "/dashboard/clientes/editar/$id/modal", params: { id: row.id } })}>
                <PencilIcon />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Excluir Cliente">
              <ActionIcon variant="light" color="red" onClick={() => handleDelete(row.id)}>
                <TrashIcon />
              </ActionIcon>
            </Tooltip>
          </ActionIcon.Group>
        );
      }
    }
  ];

  return (
    <Stack>
      <div className="flex items-center justify-between gap-[inherit]">
        <DashboardComponents.Title>Gerenciar Clientes</DashboardComponents.Title>

        <div className="flex gap-1">
          <DashboardComponents.RefreshButton onClick={refetch} tooltipProps={{ label: "Atualizar Clientes" }} loading={isFetching} />

          <Tooltip label="Novo Cliente">
            <Link to="/dashboard/clientes/cadastrar/modal" className="flex">
              <ActionIcon size="xl">
                <PlusIcon />
              </ActionIcon>
            </Link>
          </Tooltip>
        </div>
      </div>

      <DataGridBase
        rows={data}
        columns={columns}
        loading={isFetching}
        columnVisibilityModel={{
          id: false,
          code: false
        }}
      />
    </Stack>
  );
}
